import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getDashboard, getMyCourses, getBadges, markSeen, fileUrl, thumbUrl, updateProfile, uploadFileChunked } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Sun, Moon, Pencil, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import Leccion from "@/components/campus/Leccion";
import Comunidad from "@/components/campus/Comunidad";
import VipZone from "@/components/campus/VipZone";
import RankingPanel from "@/components/campus/RankingPanel";
import PortalEmpleo from "@/components/campus/PortalEmpleo";
import ZonaRecursos from "@/components/campus/ZonaRecursos";
import Solicitudes from "@/components/campus/Solicitudes";
import NotificationBell from "@/components/campus/NotificationBell";
import AnnouncementPopup from "@/components/campus/AnnouncementPopup";

const NAV = [
  ["inicio", "◈", "Inicio"],
  ["cursos", "▤", "El Máster"],
  ["ranking", "♜", "Ranking"],
  ["comunidad", "◇", "Comunidad"],
  ["empleo", "◰", "Empleo"],
  ["recursos", "❖", "Recursos"],
  ["solicitudes", "✉", "Solicitudes"],
  ["vip", "♛", "Zona VIP"],
];

export default function Campus() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const { light, toggle } = useTheme();
  const [tab, setTab] = useState("inicio");
  const [slug, setSlug] = useState(null);
  const [editName, setEditName] = useState(false);

  const { data: dash } = useQuery({ queryKey: ["dashboard"], queryFn: getDashboard });
  const { data: cursos = [], refetch } = useQuery({ queryKey: ["myCourses"], queryFn: getMyCourses });
  const { data: badges, refetch: refetchBadges } = useQuery({ queryKey: ["badges"], queryFn: getBadges, refetchInterval: 45000 });
  const [filtro, setFiltro] = useState("Todos");

  const abrirCurso = (s) => { setSlug(s); setTab("leccion"); setTimeout(refetch, 1500); };

  // Deep-link desde los avisos: /campus?curso=slug  o  /campus?tab=recursos
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const curso = p.get("curso");
    const t = p.get("tab");
    if (curso) { setSlug(curso); setTab("leccion"); }
    else if (t) { setTab(t); }
    if (curso || t) window.history.replaceState({}, "", "/campus");
  }, []);
  const openTab = (id) => {
    setTab(id);
    if (id === "empleo" || id === "recursos") {
      markSeen(id).then(() => refetchBadges()).catch(() => {});
    }
  };
  const dot = (id) => (id === "empleo" && badges?.empleo_nuevo) || (id === "recursos" && badges?.recursos_nuevo);
  const enCurso = cursos.filter((c) => c.progreso > 0);
  const cats = ["Todos", ...Array.from(new Set(cursos.map((c) => c.categoria)))];
  const lista = filtro === "Todos" ? cursos : cursos.filter((c) => c.categoria === filtro);

  const doLogout = () => { logout(); navigate("/"); };

  return (
    <div className={light ? "lci light" : "lci"}><div className="campus">
      <AnnouncementPopup />
      <aside className="side">
        <div className="side-head">
          <div className="logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
            <div className="logo-mark">L</div>
            <div className="logo-txt" style={{ fontSize: 11 }}>CAMPUS <em>IA</em>
            </div>
          </div>
        </div>
        <nav>
          {NAV.map(([id, ico, lbl]) => (
            <button key={id} data-testid={`nav-${id}`} className={`side-item ${tab === id || (id === "cursos" && tab === "leccion") ? "on" : ""}`}
              onClick={() => openTab(id)}>
              <span style={{ position: "relative" }}>{ico}{dot(id) && <span className="nav-dot" data-testid={`dot-${id}`} />}</span>{lbl}
            </button>
          ))}
        </nav>
        <div className="side-foot">
          <button className="side-item" data-testid="campus-theme-toggle" onClick={toggle}>
            <span>{light ? <Moon size={15} /> : <Sun size={15} />}</span>{light ? "Modo oscuro" : "Modo claro"}
          </button>
          {user?.role === "admin" && (
            <button className="side-item" data-testid="nav-admin" onClick={() => navigate("/admin")}><span>⚙</span>Admin</button>
          )}
          <button className="side-item" data-testid="nav-salir" onClick={doLogout}><span>←</span>Salir</button>
        </div>
      </aside>

      <main className="main">
        <NotificationBell />
        {tab === "inicio" && (
          <>
            <div className="main-head">
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                {user?.avatar
                  ? <img src={fileUrl(user.avatar)} alt="avatar" data-testid="header-avatar" style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", border: "2px solid var(--gold)" }} />
                  : <div data-testid="header-avatar" style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(197,164,90,.14)", color: "var(--gold)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700, border: "2px solid var(--gold)" }}>{(user?.name || "A").trim()[0]?.toUpperCase() || "A"}</div>}
                <div>
                  <h1>Hola, <span className="gold-it">{user?.name || "Alumno/a"}</span>
                    <button className="abtn" data-testid="editar-nombre" title="Editar perfil" onClick={() => setEditName(true)}
                      style={{ marginLeft: 10, verticalAlign: "middle", padding: "4px 8px" }}><Pencil size={13} /></button>
                  </h1>
                  <p className="sub">Continúa donde lo dejaste. La constancia es la que factura.</p>
                </div>
              </div>
              <span className="chip" data-testid="plan-chip">Plan actual: {dash?.plan || "Estándar"}</span>
            </div>
            <VideoHoursCard segs={dash?.segundos_video ?? 0} />
            <div className="kpis" data-testid="kpis">
              <div className="kpi"><div className="v" data-testid="kpi-activos">{dash?.cursos_activos ?? 0}</div><div className="k">Cursos activos</div></div>
              <div className="kpi"><div className="v">{dash?.progreso_medio ?? 0}%</div><div className="k">Progreso medio</div></div>
              <div className="kpi"><div className="v">{dash?.lecciones_vistas ?? 0}</div><div className="k">Lecciones vistas</div></div>
              <div className="kpi"><div className="v">{dash?.racha ?? 0} días</div><div className="k">Racha actual</div></div>
            </div>
            <h2 className="serif" style={{ fontSize: 22, fontWeight: 600, marginBottom: 18 }}>Continuar aprendiendo</h2>
            {enCurso.length === 0 ? (
              <p style={{ color: "var(--muted)" }}>Aún no has empezado ningún curso. Ve a <button className="link-gold" onClick={() => setTab("cursos")}>Mis cursos</button> para comenzar.</p>
            ) : (
              <div className="cards">
                {enCurso.map((c) => (
                  <div className="ccard" key={c.slug} data-testid={`continuar-${c.slug}`} onClick={() => abrirCurso(c.slug)} style={{ position: "relative" }}>
                    {c.nuevo_contenido && <span className="nuevo-circ" data-testid={`continuar-nuevo-${c.slug}`}>NUEVO<br />CONTENIDO</span>}
                    <span className="chip">{c.categoria}</span>
                    <h3>{c.titulo}</h3>
                    <div className="mini">{(c.modulos || []).length} módulos · {c.lecciones || 0} capítulos</div>
                    <div className="pbar"><i style={{ width: `${c.progreso}%` }} /></div>
                    <span className="ppct">{c.progreso}% COMPLETADO</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "cursos" && (
          <>
            <div className="main-head">
              <div>
                <h1>El Máster</h1>
                <p className="sub">Tu programa completo de IA. {user?.vip_vitalicio || user?.role !== "alumno" ? "Acceso de por vida." : ""}</p>
              </div>
            </div>
            {cursos.length === 0 ? (
              <p style={{ color: "var(--muted)" }}>Todavía no tienes acceso al Máster. <button className="link-gold" onClick={() => navigate("/#precio")}>Consíguelo aquí →</button></p>
            ) : (
              <>
                <div className="comu-tabs">
                  {cats.map((cat) => (
                    <button key={cat} data-testid={`campus-filtro-${cat}`} className={`comu-tab ${filtro === cat ? "on" : ""}`} onClick={() => setFiltro(cat)}>{cat}</button>
                  ))}
                </div>
                <div className="cards" data-testid="mis-cursos-grid">
                  {lista.map((c) => (
                    <div className="ccard" key={c.slug} data-testid={`curso-card-${c.slug}`} onClick={() => abrirCurso(c.slug)} style={{ position: "relative" }}>
                      {c.nuevo_contenido && <span className="nuevo-circ" data-testid={`nuevo-contenido-${c.slug}`}>NUEVO<br />CONTENIDO</span>}
                      {c.portada && (
                        <div className="ccard-cover img-skel" data-testid={`curso-portada-${c.slug}`}
                          style={{ backgroundImage: `url(${thumbUrl(c.portada, 800)})`, backgroundSize: "cover", backgroundPosition: "center", height: 130, borderRadius: 10, marginBottom: 12, border: "1px solid var(--line)" }} />
                      )}
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                        <span className="chip">{c.categoria}</span>
                        {c.destacado && <span className="chip-nuevo" data-testid={`badge-nuevo-${c.slug}`}>NUEVO</span>}
                        {c.acceso === "vip" && <span className="chip-vip" data-testid={`badge-vip-${c.slug}`}>VIP</span>}
                      </div>
                      <h3>{c.titulo}</h3>
                      <div className="mini">{(c.modulos || []).length} módulos · {c.lecciones || 0} capítulos</div>
                      <div className="pbar"><i style={{ width: `${c.progreso}%` }} /></div>
                      <span className="ppct">{c.progreso > 0 ? `${c.progreso}% COMPLETADO` : "EMPEZAR AHORA"}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {tab === "leccion" && slug && (
          <Leccion slug={slug} onBack={() => { setTab("cursos"); refetch(); }} />
        )}

        {tab === "comunidad" && <Comunidad nombre={user?.name} esAdmin={user?.role === "admin"} />}

        {tab === "empleo" && <PortalEmpleo />}

        {tab === "recursos" && <ZonaRecursos />}

        {tab === "solicitudes" && <Solicitudes />}

        {tab === "ranking" && <RankingPanel />}

        {tab === "vip" && <VipZone user={user} />}
      </main>
      {editName && <UsernameModal user={user} onClose={() => setEditName(false)} onSaved={refreshUser} />}
    </div></div>
  );
}

function VideoHoursCard({ segs }) {
  const h = Math.floor(segs / 3600);
  const min = Math.round((segs % 3600) / 60);
  return (
    <div className="vh-card" data-testid="video-hours-card">
      <div className="vh-ico"><PlayCircle size={26} /></div>
      <div>
        <div className="vh-k">Horas totales de vídeo</div>
        <div className="vh-v" data-testid="video-hours-value">{h}<small>h</small> {min > 0 && <>{min}<small>min</small></>}</div>
        <div className="vh-sub">de formación disponible en tus cursos</div>
      </div>
    </div>
  );
}

function UsernameModal({ user, onClose, onSaved }) {
  const [name, setName] = useState(user?.name || "");
  const [avatar, setAvatar] = useState(user?.avatar || "");
  const [saving, setSaving] = useState(false);
  const [pct, setPct] = useState(null);
  const [over, setOver] = useState(false);
  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Debe ser una imagen"); return; }
    setPct(0);
    try { const res = await uploadFileChunked(file, setPct); setAvatar(res.url); toast.success("Avatar subido ✓"); }
    catch { toast.error("Error al subir el avatar"); }
    finally { setPct(null); }
  };
  const save = async () => {
    if (name.trim().length < 3) { toast.error("Mínimo 3 caracteres"); return; }
    setSaving(true);
    try {
      await updateProfile({ name: name.trim(), avatar });
      await onSaved();
      toast.success("Perfil actualizado ✓");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };
  const inicial = (name || user?.email || "A").trim()[0]?.toUpperCase() || "A";
  return (
    <div className="amodal" onClick={onClose}>
      <div className="amodal-card" onClick={(e) => e.stopPropagation()} data-testid="username-modal" style={{ maxWidth: 440 }}>
        <h3>Tu perfil</h3>
        <p style={{ color: "var(--muted)", fontSize: 12.5, marginBottom: 14 }}>Tu avatar y tu nombre aparecen en la comunidad y el ranking. El nombre también sirve para iniciar sesión.</p>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
          <label
            data-testid="avatar-drop"
            onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); doUpload(e.dataTransfer?.files?.[0]); }}
            style={{ width: 84, height: 84, borderRadius: "50%", overflow: "hidden", cursor: pct != null ? "wait" : "pointer", flexShrink: 0,
              border: `2px ${over ? "solid var(--gold)" : "dashed var(--line)"}`, display: "flex", alignItems: "center", justifyContent: "center",
              background: avatar ? "transparent" : "rgba(197,164,90,.12)", position: "relative" }}>
            {avatar ? <img src={fileUrl(avatar)} alt="avatar" data-testid="avatar-preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ fontSize: 30, fontWeight: 700, color: "var(--gold)" }}>{inicial}</span>}
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ""; }} disabled={pct != null} />
          </label>
          <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
            {pct == null ? "Haz clic o arrastra una imagen para tu avatar" : `Subiendo… ${pct}%`}
            {avatar && <button className="abtn" data-testid="avatar-remove" style={{ display: "block", marginTop: 8 }} onClick={() => setAvatar("")}>Quitar avatar</button>}
          </div>
        </div>
        <label style={{ fontSize: 12, color: "var(--muted)" }}>Nombre de usuario</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="username-input" placeholder="Tu nombre de usuario"
          style={{ width: "100%", background: "var(--ink)", border: "1px solid var(--line)", color: "var(--cream)", padding: "12px 14px", borderRadius: 8, fontSize: 14, marginTop: 4 }} />
        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <button className="btn btn-gold btn-sm" onClick={save} disabled={saving} data-testid="username-guardar">{saving ? "Guardando…" : "Guardar"}</button>
          <button className="abtn" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}
